/**
 * useFinanceiroContaCorrente - Hook CRUD para contas correntes
 * 
 * Operações: listagem (via RPC segura), criação, edição e soft delete
 * Tabela: financeiro_conta_corrente
 * RPC: list_financeiro_contas_correntes (SECURITY DEFINER - mascara dados sensíveis)
 * 
 * @author Sistema
 * @version 2.0.0
 * RELEASE: 2026-03-14 v1.0.0 - Criação do hook
 * RELEASE: 2026-03-29 v2.0.0 - Migração para RPC segura (nunca expõe credential_id, documento_titular, metadata)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('FinanceiroContaCorrente');

/**
 * Interface segura da conta corrente (retornada pela RPC)
 * Campos sensíveis removidos: provider_credential_id, provider_account_id, documento_titular, metadata, tenant_id
 * Agência e conta são mascarados pela RPC
 */
export interface FinanceiroContaCorrente {
  id: string;
  nome: string;
  banco_codigo: string | null;
  banco_nome: string | null;
  /** Agência mascarada (ex: ****01) — dados completos apenas no backend */
  agencia_masked: string | null;
  /** Conta mascarada (ex: ****99-3) — dados completos apenas no backend */
  conta_masked: string | null;
  tipo_conta: string;
  titular: string | null;
  is_default: boolean;
  is_active: boolean;
  /** Slug do provider (ex: asaas) — sem credential_id */
  provider_slug: string | null;
  /** Ambiente do provider (sandbox/production) */
  provider_environment: string | null;
  /** Indica se tem provider vinculado (booleano seguro, sem expor credential_id) */
  has_provider: boolean;
  /**
   * Modo de checkout exibido ao pagador (contingência v1.0.0 - 2026-06-21):
   * - 'transparent' (padrão): exibe /c/:slug (checkout próprio do sistema).
   * - 'provider': exibe invoiceUrl/bankSlipUrl nativo do provider.
   * Fallback automático ocorre no frontend (resolveCheckoutUrl) caso o
   * preferido não esteja disponível.
   */
  checkout_mode: 'transparent' | 'provider';
  /**
   * Política de pagamento de parcelamentos por cartão no link público
   * (v2026-06-27 — migrado de tenant_settings para conta corrente, pois é
   * regra por provider):
   *   - 'pay_all_remaining' (default): cobra todas as parcelas em aberto
   *     numa única transação no cartão. Atualmente é a única opção
   *     suportada pelo Asaas (gateway sempre confirma todas as parcelas
   *     remanescentes ao chamar /payWithCreditCard).
   *   - 'pay_single_only': cobra apenas a parcela acessada. Indisponível
   *     para Asaas; reservado para futuros providers que suportem.
   */
  card_installment_payment_mode: 'pay_all_remaining' | 'pay_single_only';
  created_at: string;
  updated_at: string;
}

/**
 * Interface para criação/edição (dados completos — enviados ao backend)
 */
export interface ContaCorrenteFormData {
  /** Modo de checkout exibido ao pagador. Default 'transparent'. */
  checkout_mode?: 'transparent' | 'provider';
  /** Política de cartão em parcelamentos. Default 'pay_all_remaining'. */
  card_installment_payment_mode?: 'pay_all_remaining' | 'pay_single_only';
  nome: string;
  banco_codigo?: string;
  banco_nome?: string;
  agencia?: string;
  conta?: string;
  tipo_conta?: string;
  titular?: string;
  documento_titular?: string;
  saldo_inicial?: number;
  is_default?: boolean;
  is_active?: boolean;
}

/**
 * Hook para CRUD de contas correntes financeiras
 * Listagem via RPC SECURITY DEFINER para proteção de dados sensíveis
 */
export function useFinanceiroContaCorrente() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // Listagem via RPC segura (mascara agência, conta; remove credential_id, documento, metadata)
  const { data: contas = [], isLoading, error } = useQuery({
    queryKey: ['financeiro-contas-correntes', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      const { data, error } = await supabase.rpc('list_financeiro_contas_correntes', {
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao buscar contas correntes via RPC', { error: error.message });
        throw error;
      }

      return (data || []) as FinanceiroContaCorrente[];
    },
    enabled: !!tenantId,
  });

  // Criação de conta corrente (INSERT direto — dados completos necessários)
  const createConta = useMutation({
    mutationFn: async (formData: ContaCorrenteFormData) => {
      if (!tenantId) throw new Error('Tenant não encontrado');

      const { data, error } = await supabase
        .from('financeiro_conta_corrente')
        .insert({
          tenant_id: tenantId,
          ...formData,
        })
        .select()
        .single();

      if (error) {
        logger.error('Erro ao criar conta corrente', { error: error.message });
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financeiro-contas-correntes'] });
      toast.success('Conta corrente criada com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar conta corrente: ${error.message}`);
    },
  });

  // Edição de conta corrente
  const updateConta = useMutation({
    mutationFn: async ({ id, ...formData }: ContaCorrenteFormData & { id: string }) => {
      const { data, error } = await supabase
        .from('financeiro_conta_corrente')
        .update(formData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logger.error('Erro ao atualizar conta corrente', { error: error.message });
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financeiro-contas-correntes'] });
      toast.success('Conta corrente atualizada com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar conta corrente: ${error.message}`);
    },
  });

  // Soft delete de conta corrente
  const deleteConta = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('financeiro_conta_corrente')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        logger.error('Erro ao excluir conta corrente', { error: error.message });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financeiro-contas-correntes'] });
      toast.success('Conta corrente excluída com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir conta corrente: ${error.message}`);
    },
  });

  return {
    contas,
    isLoading,
    error,
    createConta,
    updateConta,
    deleteConta,
  };
}
